import { useCallback, useState } from "react";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { getManualAssessmentReportPresignedUrl } from "../../services/manualAssessmentService";
import { fileNameFromAttachmentS3Uri } from "../../utils/manualAssessmentReportShare";

function fileNameFromPresignedUrl(
  presignedUrl: string,
  fallback: string,
): string {
  try {
    const urlObj = new URL(presignedUrl);
    const disposition =
      urlObj.searchParams.get("response-content-disposition") || "";
    const filenameMatch = /filename="([^"]+)"/.exec(disposition);
    if (filenameMatch?.[1]) return filenameMatch[1];
    const filenameStar = /filename\*=UTF-8''(.+)/.exec(disposition);
    if (filenameStar?.[1]) return decodeURIComponent(filenameStar[1]);
  } catch {
    return fallback;
  }
  return fallback;
}

/**
 * View / download the Manual IA PDF via POST /manual-assessment/reports/user-presigned-url.
 */
export function useManualIaPdfRowActions() {
  const { showMessage } = useSnackbar();
  const [busyFilePath, setBusyFilePath] = useState<string | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState("");

  const previewPdf = useCallback(
    async (filePath: string) => {
      const trimmed = filePath.trim();
      if (!trimmed) return;
      try {
        const { presigned_url } =
          await getManualAssessmentReportPresignedUrl(trimmed, "inline");
        setSelectedPdfUrl(presigned_url);
        setPdfDialogOpen(true);
      } catch (error) {
        console.error("Error previewing Manual IA PDF:", error);
        const msg =
          error instanceof Error ? error.message : "Failed to load PDF preview.";
        showMessage(msg, "error");
      }
    },
    [showMessage],
  );

  const downloadPdf = useCallback(
    async (filePath: string) => {
      const trimmed = filePath.trim();
      if (!trimmed || busyFilePath) return;
      setBusyFilePath(trimmed);
      try {
        const { presigned_url, filename: backendFilename } =
          await getManualAssessmentReportPresignedUrl(trimmed, "attachment");
        const fileName =
          backendFilename ||
          fileNameFromPresignedUrl(
            presigned_url,
            fileNameFromAttachmentS3Uri(trimmed),
          );

        const xhr = new XMLHttpRequest();
        xhr.open("GET", presigned_url, true);
        xhr.responseType = "blob";
        xhr.onload = () => {
          if (xhr.status < 200 || xhr.status >= 300) {
            showMessage("Failed to download the report.", "error");
            setBusyFilePath(null);
            return;
          }
          const blobUrl = URL.createObjectURL(xhr.response);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            setBusyFilePath(null);
          }, 100);
        };
        xhr.onerror = () => {
          console.error("Error downloading Manual IA PDF via XHR");
          showMessage("Failed to download the report.", "error");
          setBusyFilePath(null);
        };
        xhr.send();
      } catch (error) {
        console.error("Error downloading Manual IA PDF:", error);
        const msg =
          error instanceof Error ? error.message : "Failed to download the report.";
        showMessage(msg, "error");
        setBusyFilePath(null);
      }
    },
    [busyFilePath, showMessage],
  );

  return {
    previewPdf,
    downloadPdf,
    downloadingFilePath: busyFilePath,
    pdfDialogOpen,
    selectedPdfUrl,
    closePdfDialog: () => setPdfDialogOpen(false),
  };
}
